import type { FrameLoading, Tour, TourFrame } from '@carboncopyco/types';
import { type Slice, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface TourCreatorState {
  isCapturing: boolean;
  isReadyToCapture: boolean;
  tourId: Tour['id'] | null;
  tourFrames: TourFrame[];
  isCapturingFinished: boolean;
  fileServiceToken: string | null;
  canFinishCapturing: boolean;
  isCaptureStarted: boolean;
  isCaptureButtonDisabled: boolean;
  framesLoading: FrameLoading[] | [];
  isCaptureSnapshotPending: boolean;
  isShowingDimensionPanel: boolean;
}

interface TourCreatorSlice {
  tours: {
    [uuid: string]: TourCreatorState;
  };
}

type ToursCreatorAction<T = {}> = PayloadAction<T & { uuid: string }>;

const tourInitialState: TourCreatorState = {
  tourFrames: [],
  framesLoading: [],
  tourId: null,
  fileServiceToken: null,
  isCapturing: false,
  isReadyToCapture: false,
  isCapturingFinished: false,
  canFinishCapturing: true,
  isCaptureStarted: false,
  isCaptureButtonDisabled: false,
  isCaptureSnapshotPending: false,
  isShowingDimensionPanel: false,
};

const tourCreatorInitialState: TourCreatorSlice = {
  tours: {},
};

export const tourCreatorSlice: Slice<TourCreatorSlice> = createSlice({
  name: 'tourCreator',
  initialState: tourCreatorInitialState,
  reducers: {
    setInitialTourCreatorState: (state, action: ToursCreatorAction) => {
      state.tours[action.payload.uuid] = tourInitialState;
    },
    clearTourCreatorState: (state, action: ToursCreatorAction) => {
      delete state.tours[action.payload.uuid];
    },
    setIsShowingDimensionPanel: (
      state,
      action: ToursCreatorAction<{
        isShowingDimensionPanel: TourCreatorState['isShowingDimensionPanel'];
      }>
    ) => {
      state.tours[action.payload.uuid].isShowingDimensionPanel =
        action.payload.isShowingDimensionPanel;
    },
    setIsCapturing: (
      state,
      action: ToursCreatorAction<{
        isCapturing: TourCreatorState['isCapturing'];
      }>
    ) => {
      state.tours[action.payload.uuid].isCapturing = action.payload.isCapturing;
    },
    setTourId: (
      state,
      action: ToursCreatorAction<{
        tourId: TourCreatorState['tourId'];
      }>
    ) => {
      state.tours[action.payload.uuid].tourId = action.payload.tourId;
    },
    addTourFrame: (
      state,
      action: ToursCreatorAction<{ tourFrame: TourFrame }>
    ) => {
      state.tours[action.payload.uuid].tourFrames.push(
        action.payload.tourFrame
      );
    },
    setIsReadyToCapture: (state, action: ToursCreatorAction) => {
      state.tours[action.payload.uuid].isReadyToCapture = true;
    },
    resetTourFrames: (state, action: ToursCreatorAction) => {
      state.tours[action.payload.uuid].tourFrames = [];
    },
    removeLastFrame: (state, action: ToursCreatorAction) => {
      const uuid = action.payload.uuid;
      state.tours[uuid].tourFrames = state.tours[uuid].tourFrames.slice(
        0,
        state.tours[uuid].tourFrames.length - 1
      );
    },
    setIsCapturingFinished: (
      state,
      action: ToursCreatorAction<{
        isCapturingFinished: TourCreatorState['isCapturingFinished'];
      }>
    ) => {
      state.tours[action.payload.uuid].isCapturingFinished =
        action.payload.isCapturingFinished;
    },
    setTourRecordingFinished: (
      state,
      action: ToursCreatorAction<{ isFinished: boolean }>
    ) => {
      state.tours[action.payload.uuid].isCapturingFinished =
        action.payload.isFinished;
      state.tours[action.payload.uuid].isCapturing = !action.payload.isFinished;
    },
    resetTourCreator: (state, action: ToursCreatorAction) => {
      state.tours[action.payload.uuid] = tourInitialState;
    },
    setCanFinishCapturing: (
      state,
      action: ToursCreatorAction<{
        canFinish: TourCreatorState['canFinishCapturing'];
      }>
    ) => {
      state.tours[action.payload.uuid].canFinishCapturing =
        action.payload.canFinish;
    },
    setIsCaptureStarted: (
      state,
      action: ToursCreatorAction<{
        isStarted: TourCreatorState['isCaptureStarted'];
      }>
    ) => {
      state.tours[action.payload.uuid].isCaptureStarted =
        action.payload.isStarted;
    },
    setIsCaptureButtonDisabled: (
      state,
      action: ToursCreatorAction<{
        disabled: TourCreatorState['isCaptureButtonDisabled'];
      }>
    ) => {
      state.tours[action.payload.uuid].isCaptureButtonDisabled =
        action.payload.disabled;
    },
    updateFramesLoading: (
      state,
      action: ToursCreatorAction<{
        frameLoading: FrameLoading;
      }>
    ) => {
      const { frameIndex, isLoading, progress } = action.payload.frameLoading;
      state.tours[action.payload.uuid].framesLoading[frameIndex] = {
        frameIndex,
        isLoading,
        progress,
      };
    },
    setIsCaptureSnapshotPending: (
      state,
      action: ToursCreatorAction<{
        isPending: TourCreatorState['isCaptureSnapshotPending'];
      }>
    ) => {
      state.tours[action.payload.uuid].isCaptureSnapshotPending =
        action.payload.isPending;
    },
  },
});

export const {
  setInitialTourCreatorState,
  clearTourCreatorState,
  setIsShowingDimensionPanel,
  setIsCapturing,
  addTourFrame,
  removeLastFrame,
  setIsReadyToCapture,
  setTourId,
  resetTourFrames,
  setIsCapturingFinished,
  setTourRecordingFinished,
  resetTourCreator,
  setCanFinishCapturing,
  setIsCaptureStarted,
  setIsCaptureButtonDisabled,
  updateFramesLoading,
  setIsCaptureSnapshotPending,
} = tourCreatorSlice.actions;
